import { getUrlParams } from '../../../utils';
import { API } from '../../../services';

export const formItems = {
    applicantYearlyIncome: 'applicantYearlyIncome',
    applicantAge: 'applicantAge',
    mortageAmount: 'mortageAmount',
    mortgageType: 'mortgageType',
    hasPartnerIncome: 'hasPartnerIncome',
    partnerYearlyIncome: 'partnerYearlyIncome',
    partnerAge: 'partnerAge',
    monthlyCostsGross: 'monthlyCostsGross',
    monthlyCostsClean: 'monthlyCostsClean',
    ownMoney: 'ownMoney',
    askPrice: 'askPrice',
};
export interface DefaultValues {
    applicantAge: number;
    applicantYearlyIncome: number;
    partnerAge: number;
    partnerYearlyIncome: number;
    hasPartnerIncome: boolean;
    askPrice: number;
    askPriceNumber: number;
    ownMoney: null | number;
    monthlyCostsGross: number;
    monthlyCostsClean: number;
    postcode: string;
    huisnummer: string;
    huisNummerToevoeging: number;
    isNieuwBouw: boolean;
    mortageAmount: number;
}

export const getDefaultValues = (dataOverwrite: any = {}) => {
    const askPrice = getUrlParams('vraagprijs');
    const askPriceNumber = askPrice !== null && askPrice !== '' ? parseInt(askPrice) : 250000;
    const buildingType = getUrlParams('postcode');

    const data = {
        [formItems.applicantAge]: 35,
        [formItems.applicantYearlyIncome]: 40000,
        [formItems.partnerAge]: 35,
        [formItems.partnerYearlyIncome]: 40000,
        [formItems.hasPartnerIncome]: false,
        [formItems.askPrice]: askPriceNumber,
        [formItems.mortageAmount]: askPriceNumber,
        [formItems.ownMoney]: null,
        [formItems.monthlyCostsGross]: 0,
        [formItems.monthlyCostsClean]: 0,
        postcode: getUrlParams('postcode') || '',
        huisnummer: getUrlParams('huisnummer') || '',
        huisnummerToevoeging: getUrlParams('huisnummerToevoeging') || '',
        isNieuwBouw: buildingType != null && buildingType === 'nieuwbouw' ? true : false,
        ...dataOverwrite,
    };

    if (askPriceNumber - data[formItems.mortageAmount] > 0) {
        data[formItems.ownMoney] = askPriceNumber - data[formItems.mortageAmount];
    }

    if (data[formItems.mortageAmount] == null) {
        data[formItems.mortageAmount] = 10000;
    }

    return data;
};

interface MortgageProvider {
    aanbiederNaam: string;
    rentestand: number;
    hypotheekVorm: string;
    rentevastePeriode: number;
}

interface MortgageProvidersResponse {
    producten: MortgageProvider[];
    laatstBijgewerkt: string;
}

interface NhgLimits {
    existingBuilding: number;
    newBuilding: number;
    withEnergyEfficiencyMeasures: number;
}

const DEFAULT_FIXED_INTEREST_PERIOD = 10;
// Only used when the interest rate API is unreachable, check against the current rates periodically
const FALLBACK_INTEREST_RATE = 4.0;

function getHypotheekVormParam(mortgageType: number): string {
    switch (mortgageType) {
        case 9:
            return 'Lineaire_hypotheek';
        case 4:
        default:
            return 'Annuiteitenhypotheek';
    }
}

/**
 * Determines the rate basis for the interest rates request.
 * Mirrors calculationToolUtilsResultCalculateRenteBasis in react-tools: 0 = NHG rate, 101 = above 100% market value
 */
export function getRenteBasis(mortgageAmount: number, nhgLimit: number | null): number {
    if (!mortgageAmount || mortgageAmount <= 0) {
        return 0;
    }

    // Check if the mortgage qualifies for NHG (National Mortgage Guarantee)
    if (nhgLimit && mortgageAmount <= nhgLimit) {
        return 0;
    }

    return 101;
}

/**
 * Picks the lowest interest rate to get the best possible monthly cost.
 * Mirrors calculationToolUtilsResultGetLowestInterestRateProvider in react-tools
 */
export function getLowestInterestRate(providers: MortgageProvider[]): number | null {
    const rates = providers.map((provider) => provider.rentestand).filter((rate) => rate > 0);

    return rates.length ? Math.min(...rates) : null;
}

// The NHG limits only change once a year, so fetch them once per session
let cachedNhgLimit: number | null | undefined;

async function fetchNhgLimit(): Promise<number | null> {
    if (cachedNhgLimit !== undefined) {
        return cachedNhgLimit;
    }

    try {
        const nhg = await API.get<NhgLimits>('nhg');
        cachedNhgLimit = nhg.existingBuilding || null;
    } catch (err) {
        console.log(err);
        // No NHG limit known, fall back to the non NHG rate basis
        cachedNhgLimit = null;
    }

    return cachedNhgLimit;
}

export async function fetchLowestInterestRate(mortgageType: number, mortgageAmount: number): Promise<number> {
    try {
        const nhgLimit = await fetchNhgLimit();
        const params = new URLSearchParams({
            HypotheekVorm: getHypotheekVormParam(mortgageType),
            IsNieuwbouw: 'false',
            RenteBasis: String(getRenteBasis(mortgageAmount, nhgLimit)),
            RentevastePeriode: String(DEFAULT_FIXED_INTEREST_PERIOD),
            EnergyLabel: 'A',
            ForCalculation: 'true',
        });

        const response = await API.get<MortgageProvidersResponse>(`interestrates/hypotheekaanbieders?${params.toString()}`);
        const lowestInterestRate = getLowestInterestRate(response.producten || []);

        return lowestInterestRate !== null ? lowestInterestRate : FALLBACK_INTEREST_RATE;
    } catch (err) {
        console.log(err);
        return FALLBACK_INTEREST_RATE;
    }
}

export function transformCalculationDataForApi(data: any = {}, interestRate: number = FALLBACK_INTEREST_RATE) {
    let postData: { [id: string]: any } = {
        mortgageType: Number(data[formItems.mortgageType]),
        applicant: {
            age: Number(data[formItems.applicantAge]),
            yearlyIncome: Number(data[formItems.applicantYearlyIncome]),
            statePensionIncome: 0,
            oldAgePensionIncome: 0,
        },
        mortageAmount: Number(data[formItems.mortageAmount]),
        interestRate,
        fixedInterestPeriod: DEFAULT_FIXED_INTEREST_PERIOD,
        loans: 0,
        alimony: 0,
    };

    if (data[formItems.hasPartnerIncome] === 'true') {
        postData.partner = {
            age: Number(data[formItems.partnerAge]),
            yearlyIncome: Number(data[formItems.partnerYearlyIncome]),
            statePensionIncome: 0,
            oldAgePensionIncome: 0,
        };
    }

    return postData;
}
